import {
  AppLayout,
  Button,
  Checkbox,
  Container,
  ContentLayout,
  Flashbar,
  FormField,
  Header,
  HelpPanel,
  SpaceBetween,
  Textarea,
  TextContent,
} from '@amzn/awsui-components-react';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useLocalizedText } from '../../hooks/localizedText';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { AmbassadorOffboardingHelp } from './ambassadorOffboardingHelp';
import { useOffboardAmbassador } from '../../hooks/ambassadors/ambassadorCreation/offboardAmbassadorMutation';
import { useUser } from '../../hooks/user';
import { AmbassadorSelect } from '../attendance/editEvent/ambassadors';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import { publishKatalMetric } from '../katalAnalytics';
import { featureIsEnabled, useFeatures } from '../../hooks/features';
import { NAVIGATION_REDESIGN } from '../../utils/constants';
import { AppOutletContext } from '../../App';

interface AmbassadorOffboardingFormProps {
  setSubmitted: Dispatch<SetStateAction<boolean>>;
}

/**
 * The values persisted as `offboardingReasons`, keyed to the checkbox each one belongs to.
 * Locale-independent by design: these are the English strings the form has always sent, so records
 * written before and after localization stay comparable. The checkbox label is translated for
 * display only and must never reach the request — otherwise a German ambassador's exit reason is
 * stored in German and the reporting on it fragments by UI language.
 */
const OFFBOARDING_REASONS = {
  tooTimeConsuming: 'Too time consuming',
  rotatesAmbassadors: 'My team rotates ambassadors',
  iDontEnjoyThisRole: 'I don’t enjoy this role',
  changingSiteTeamOrElg: 'I’m changing team/site or no longer a member of an employee led group',
  other: 'Other',
} as const;

/**
 * The offboarding Help panel, mirroring the codebase's *HelpPanel components (WaitlistInfoHelpPanel).
 * It reads its own header text rather than having it interpolated at the call site, because the form
 * publishes this panel into the shared AppLayout from an effect keyed on the nav flag: an element that
 * had already resolved t() would keep whatever string the first render produced, and the ARB pack
 * arrives after it.
 */
function AmbassadorOffboardingHelpPanel() {
  const { t } = useLocalizedText(['ambassadors', 'translation']);

  return (
    <HelpPanel header={<h2>{t('ambassadorOffboarding.help.beforeYouGo')}</h2>}>
      <AmbassadorOffboardingHelp />
    </HelpPanel>
  );
}

export function AmbassadorOffboardingForm({ setSubmitted }: AmbassadorOffboardingFormProps) {
  const { t } = useLocalizedText(['ambassadors', 'translation']);
  const loggedInUser = useUser();
  const [searchParams] = useSearchParams();
  const features = useFeatures();
  const isNewNav = featureIsEnabled(NAVIGATION_REDESIGN, features, searchParams);
  const outletContext = useOutletContext<AppOutletContext | null>();

  const [failed, setFailed] = useState(false);

  const [tooTimeConsuming, setTooTimeConsuming] = useState<string | undefined>(undefined);
  const [rotatesAmbassadors, setRotatesAmbassadors] = useState<string | undefined>(undefined);
  const [iDontEnjoyThisRole, setIDontEnjoyThisRole] = useState<string | undefined>(undefined);
  const [imChangingSiteTeamOrElg, setImChangingSiteTeamOrElg] = useState<string | undefined>(
    undefined
  );
  const [leavingForOtherReason, setLeavingForOtherReason] = useState<string | undefined>(undefined);
  const [selectedReplacement, setSelectedReplacement] = useState<OptionDefinition | undefined>(
    undefined
  );

  const [feedback, setFeedback] = useState('');
  const [whyGoodFit, setWhyGoodFit] = useState<string | undefined>('');

  const offboardAmbassador = useOffboardAmbassador();

  const toolsPanel = <AmbassadorOffboardingHelpPanel />;

  // Set tools panel content when in new nav mode (user can open/close freely).
  // Deliberately NOT calling setToolsOpen(true) — previous behavior auto-opened the panel
  // on mount, causing a reopen loop when the user tried to close it.
  useEffect(() => {
    if (isNewNav && outletContext) {
      outletContext.setTools(toolsPanel);
    }
    return () => {
      if (isNewNav && outletContext) {
        outletContext.setTools(null);
        outletContext.setToolsOpen(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewNav]);

  // New nav: publish the submit-failure alert into the root AppLayout's notifications region. The
  // shared layout owns the only notifications slot, so pages push into it rather than rendering a
  // Flashbar inside their content (CR-291797692 rev 8). Keyed on `failed` (the data) so the effect
  // doesn't re-run every render; cleared on unmount so the alert doesn't leak to the next page.
  useEffect(() => {
    if (!isNewNav || !outletContext) return;
    outletContext.setNotifications(
      failed ? (
        <Flashbar
          items={[
            {
              type: 'error',
              dismissible: true,
              onDismiss: () => setFailed(false),
              content: t('ambassadorOffboarding.error'),
              id: 'message_1',
            },
          ]}
        />
      ) : null
    );
    return () => outletContext.setNotifications(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewNav, failed]);

  const isValid = useMemo(() => {
    const hasPickedAReason =
      tooTimeConsuming ||
      rotatesAmbassadors ||
      iDontEnjoyThisRole ||
      imChangingSiteTeamOrElg ||
      leavingForOtherReason;

    return hasPickedAReason;
  }, [
    tooTimeConsuming,
    rotatesAmbassadors,
    iDontEnjoyThisRole,
    imChangingSiteTeamOrElg,
    leavingForOtherReason,
  ]);

  const isLoading = offboardAmbassador.isPending;

  const onSubmit = () => {
    const offboardingReasons = [
      tooTimeConsuming,
      rotatesAmbassadors,
      iDontEnjoyThisRole,
      imChangingSiteTeamOrElg,
      leavingForOtherReason,
    ].filter(Boolean) as string[];

    if (loggedInUser.employeeId) {
      const offboardRequest = {
        employeeId: loggedInUser.employeeId,
        offboardingReasons: offboardingReasons,
        offboardingAdditionalFeedback: feedback,
        offboardingNomineeAlias: selectedReplacement?.description,
        offboardingNomineeJustification: whyGoodFit,
      };

      offboardAmbassador.mutate(offboardRequest, {
        onSuccess: () => {
          publishKatalMetric('offboard_ambassador_success', JSON.stringify(offboardRequest));
          setSubmitted(true);
        },
        onError: (err) => {
          publishKatalMetric(
            'offboard_ambassador_error',
            JSON.stringify({ ...offboardRequest, error: err?.message })
          );
          setFailed(true);
          console.error(`Error offboarding ambassador: ${err}`);
        },
      });
    } else {
      setFailed(true);
      console.error(`Error offboarding ambassador: No Person ID found for current user`);
    }
  };

  const notifications = (
    <Flashbar
      items={
        failed
          ? [
              {
                type: 'error',
                dismissible: true,
                onDismiss: () => setFailed(false),
                content: t('ambassadorOffboarding.error'),
                id: 'message_1',
              },
            ]
          : []
      }
    />
  );

  const content = (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t('ambassadorOffboarding.thankYouForYourMeaningfulContributions')}
          data-testid="ambassador-offboard-page-header"
        >
          {t('ambassadorOffboarding.leaveTheCommunityAmbassadorProgram')}
        </Header>
      }
    >
      <Container
        header={
          <Header variant="h2" description={t('ambassadorOffboarding.wereSorryToSeeYouGo')}>
            {t('ambassadorOffboarding.leaveTheAmbassadorProgram')}
          </Header>
        }
      >
        <SpaceBetween direction="vertical" size="m">
          <FormField label={t('ambassadorOffboarding.whyAreYouLeavingTheProgram')}>
            <SpaceBetween direction="vertical" size={'s'}>
              <Checkbox
                onChange={({ detail }) =>
                  setTooTimeConsuming((v) => (v ? undefined : OFFBOARDING_REASONS.tooTimeConsuming))
                }
                checked={!!tooTimeConsuming}
                controlId="ambassador-offboard-reason-time-consuming-checkbox"
              >
                {t('ambassadorOffboarding.tooTimeConsuming')}
              </Checkbox>
              <Checkbox
                onChange={({ detail }) =>
                  setRotatesAmbassadors((v) =>
                    v ? undefined : OFFBOARDING_REASONS.rotatesAmbassadors
                  )
                }
                checked={!!rotatesAmbassadors}
              >
                {t('ambassadorOffboarding.myTeamRotatesAmbassadors')}
              </Checkbox>
              <Checkbox
                onChange={({ detail }) =>
                  setIDontEnjoyThisRole((v) =>
                    v ? undefined : OFFBOARDING_REASONS.iDontEnjoyThisRole
                  )
                }
                checked={!!iDontEnjoyThisRole}
              >
                {t('ambassadorOffboarding.iDontEnjoyThisRole')}
              </Checkbox>
              <Checkbox
                onChange={({ detail }) =>
                  setImChangingSiteTeamOrElg((v) =>
                    v ? undefined : OFFBOARDING_REASONS.changingSiteTeamOrElg
                  )
                }
                checked={!!imChangingSiteTeamOrElg}
              >
                {t('ambassadorOffboarding.changingSiteTeamOrLeftElg')}
              </Checkbox>
              <Checkbox
                onChange={({ detail }) =>
                  setLeavingForOtherReason((v) => (v ? undefined : OFFBOARDING_REASONS.other))
                }
                checked={!!leavingForOtherReason}
              >
                {t('ambassadorOffboarding.other')}
              </Checkbox>
            </SpaceBetween>
          </FormField>
          {leavingForOtherReason && (
            <FormField
              stretch
              label={t('ambassadorOffboarding.additionalFeedback')}
              description={t('ambassadorOffboarding.optional')}
            >
              <Textarea
                onChange={({ detail }) => setFeedback(detail.value)}
                value={feedback}
                placeholder={t('ambassadorOffboarding.tellUsMoreAboutYourExperience')}
              />
              <TextContent>
                <small>{t('ambassadorOffboarding.yourFeedback')}</small>
              </TextContent>
            </FormField>
          )}

          <div>
            <FormField
              label={t('ambassadorOffboarding.nominate')}
              description={t('ambassadorOffboarding.optional')}
            />
            <div
              className="ambassador-representation-checkbox"
              data-testid="ambassador-representation-checkbox"
            >
              <SpaceBetween direction="vertical" size="m">
                <FormField stretch label={t('ambassadorOffboarding.nomineeName')}>
                  <AmbassadorSelect
                    testId="ambassador-offboarding-nominee-alias-select"
                    currentAmbassadors={`[${selectedReplacement?.value}]` || '[]'}
                    onChange={({ detail }) => {
                      setSelectedReplacement(
                        !selectedReplacement ? detail.selectedOptions[0] : detail.selectedOptions[1]
                      );
                    }}
                  />
                </FormField>
                <FormField stretch label={t('ambassadorOffboarding.whyWouldTheyBeAGoodFit')}>
                  <Textarea
                    onChange={({ detail }) => setWhyGoodFit(detail.value)}
                    value={whyGoodFit || ''}
                    placeholder={t('ambassadorOffboarding.tellUsAboutYourNominee')}
                    data-testid="ambassador-offboarding-nominee-justification-textarea"
                  />
                </FormField>
              </SpaceBetween>
            </div>
          </div>
          <Button
            data-testid="ambassador-offboard-leave-button"
            variant="normal"
            onClick={onSubmit}
            disabled={!isValid}
            loading={isLoading}
          >
            {t('ambassadorOffboarding.leaveAmbassadorProgram')}
          </Button>
        </SpaceBetween>
      </Container>
    </ContentLayout>
  );

  // New navigation: return content only. The failure alert is published to the root AppLayout's
  // notifications region by the effect above, so it renders in the proper notification area.
  if (isNewNav) {
    return content;
  }

  // Legacy: keep existing AppLayout wrapper
  return (
    <AppLayout
      navigationHide
      toolsOpen={true}
      tools={toolsPanel}
      notifications={notifications}
      content={content}
    />
  );
}
